/**
 * How a caller waits for a car, as rows two screens share.
 *
 * The firm's own telephony settings and the platform operator's tenant list both edit the
 * same fifteen values, so they both draw them from here. Two copies of a fifteen-field
 * form is two copies that drift, and the one that drifts quietly is the one that lets
 * somebody park four callers on a four-channel trunk.
 *
 * It renders `SettingRow`s and nothing around them, and takes everything it needs as
 * props: the platform screen mounts it outside a settings page, so it must not reach for a
 * page's state, its formats or its save bar.
 *
 * Fields that mean nothing under the chosen flow are disabled rather than hidden. A firm
 * deciding whether to switch away from Classic wants to see what they would be deciding
 * about, and a hidden field is a setting nobody knows they have.
 */

import { useTranslation } from 'react-i18next';

import {
  callFlows,
  holdExpiries,
  telephonyLimits,
  waitingReminderRange,
  type CallFlow,
  type CallFlowSettings,
  type WhenHoldExpires,
} from '../api';
import { Input, Measured, Select, SettingRow, Switch } from '../../ui';

/**
 * What a control that means nothing under the chosen flow looks like.
 *
 * `Switch` dims itself; an input given an explicit colour does not, because an author's
 * `color` beats the browser's own disabled shade. A field that is inert but looks exactly
 * like the one above it is a field somebody types into twice before believing it.
 */
const DIMMED = 'disabled:opacity-60 disabled:cursor-not-allowed';

/** The same, for `Measured`, whose className lands on the wrapper rather than the input. */
const DIMMED_GROUP = 'has-[:disabled]:opacity-60';

export function CallFlowFields({
  settings,
  onChange,
  idPrefix = '',
}: {
  settings: CallFlowSettings;
  onChange: (settings: CallFlowSettings) => void;
  /** Distinguishes the ids when more than one of these is on a page at once. */
  idPrefix?: string;
}) {
  const { t } = useTranslation();

  const id = (name: string) => `${idPrefix}${name}`;

  function set<K extends keyof CallFlowSettings>(key: K, value: CallFlowSettings[K]) {
    onChange({ ...settings, [key]: value });
  }

  // Nothing below the flow select means anything on Classic; and on RingBackOnly nobody
  // is parked, so the hold, line-count and reminder fields describe something that never
  // happens.
  const announces = settings.callFlow !== 'Classic';
  const holds = settings.callFlow === 'HoldAndAnnounce';

  return (
    <>
      <SettingRow
        label={t('telephony.callFlow')}
        help={
          <>
            <div>{t('telephony.callFlowHelp')}</div>
            {/* The chosen flow explains itself, here, rather than in a paragraph that
                describes whichever one was chosen last. */}
            <div className="mt-1">{t(`telephony.flowHelp.${settings.callFlow}`)}</div>
          </>
        }
      >
        <Select
          id={id('callFlow')}
          aria-label={t('telephony.callFlow')}
          value={settings.callFlow}
          className={DIMMED}
          onChange={(e) => set('callFlow', e.target.value as CallFlow)}
        >
          {callFlows.map((flow) => (
            <option key={flow} value={flow}>
              {t(`telephony.flow.${flow}`)}
            </option>
          ))}
        </Select>
      </SettingRow>

      <NumberRow
        id={id('trunkChannels')}
        name="trunkChannels"
        value={settings.trunkChannels}
        limits={telephonyLimits.trunkChannels}
        unit={t('telephony.unitChannels')}
        disabled={!holds}
        onChange={(value) => set('trunkChannels', value)}
      />

      <NumberRow
        id={id('trunkChannelsKeptFree')}
        name="trunkChannelsKeptFree"
        value={settings.trunkChannelsKeptFree}
        limits={{ min: 0, max: settings.trunkChannels }}
        unit={t('telephony.unitChannels')}
        disabled={!holds}
        /* The number the whole capacity rule comes down to, worked out where somebody can
           see it — a firm that types four and four wants to know it has just decided to
           hold nobody. */
        note={
          holds
            ? t('telephony.maxHeldOnTrunk', {
                count: Math.max(0, settings.trunkChannels - settings.trunkChannelsKeptFree),
              })
            : undefined
        }
        onChange={(value) => set('trunkChannelsKeptFree', value)}
      />

      <SettingRow label={t('telephony.simLinePattern')} help={t('telephony.simLinePatternHelp')}>
        <Input
          id={id('simLinePattern')}
          type="text"
          aria-label={t('telephony.simLinePattern')}
          value={settings.simLinePattern}
          placeholder="gsm"
          disabled={!holds}
          className={DIMMED}
          onChange={(e) => set('simLinePattern', e.target.value)}
        />
      </SettingRow>

      <NumberRow
        id={id('maxHeldOnSims')}
        name="maxHeldOnSims"
        value={settings.maxHeldOnSims}
        limits={telephonyLimits.maxHeldOnSims}
        unit={t('telephony.unitCallers')}
        disabled={!holds}
        onChange={(value) => set('maxHeldOnSims', value)}
      />

      <NumberRow
        id={id('holdTimeoutSeconds')}
        name="holdTimeoutSeconds"
        value={settings.holdTimeoutSeconds}
        limits={telephonyLimits.holdTimeoutSeconds}
        unit={t('settings.unitSeconds')}
        disabled={!holds}
        onChange={(value) => set('holdTimeoutSeconds', value)}
      />

      {/* Zero is off and the range starts at twenty, so this field jumps rather than
          sliding through values the API refuses. */}
      <SettingRow
        label={t('telephony.waitingReminderSeconds')}
        help={t('telephony.waitingReminderSecondsHelp')}
      >
        <Measured
          id={id('waitingReminderSeconds')}
          type="number"
          inputMode="numeric"
          aria-label={t('telephony.waitingReminderSeconds')}
          unit={t('settings.unitSeconds')}
          value={settings.waitingReminderSeconds}
          min={waitingReminderRange.off}
          max={waitingReminderRange.max}
          step={5}
          disabled={!holds}
          className={DIMMED_GROUP}
          onChange={(e) => set('waitingReminderSeconds', e.target.valueAsNumber)}
        />
      </SettingRow>

      <SettingRow label={t('telephony.whenHoldExpires')} help={t('telephony.whenHoldExpiresHelp')}>
        <Select
          id={id('whenHoldExpires')}
          aria-label={t('telephony.whenHoldExpires')}
          value={settings.whenHoldExpires}
          disabled={!holds}
          className={DIMMED}
          onChange={(e) => set('whenHoldExpires', e.target.value as WhenHoldExpires)}
        >
          {holdExpiries.map((expiry) => (
            <option key={expiry} value={expiry}>
              {t(`telephony.holdExpiry.${expiry}`)}
            </option>
          ))}
        </Select>
      </SettingRow>

      <SettingRow
        label={t('telephony.ringBackWhenCallerHangsUp')}
        help={t('telephony.ringBackWhenCallerHangsUpHelp')}
      >
        <Switch
          checked={settings.ringBackWhenCallerHangsUp}
          label={t('telephony.ringBackWhenCallerHangsUp')}
          disabled={!holds}
          onChange={(next) => set('ringBackWhenCallerHangsUp', next)}
        />
      </SettingRow>

      <NumberRow
        id={id('ringBackAttempts')}
        name="ringBackAttempts"
        value={settings.ringBackAttempts}
        limits={telephonyLimits.ringBackAttempts}
        unit={t('telephony.unitAttempts')}
        disabled={!announces}
        onChange={(value) => set('ringBackAttempts', value)}
      />

      <NumberRow
        id={id('ringBackRetrySeconds')}
        name="ringBackRetrySeconds"
        value={settings.ringBackRetrySeconds}
        limits={telephonyLimits.ringBackRetrySeconds}
        unit={t('settings.unitSeconds')}
        disabled={!announces || settings.ringBackAttempts === 0}
        onChange={(value) => set('ringBackRetrySeconds', value)}
      />

      <SettingRow
        label={t('telephony.announceArrivalTime')}
        help={t('telephony.announceArrivalTimeHelp')}
      >
        <Switch
          checked={settings.announceArrivalTime}
          label={t('telephony.announceArrivalTime')}
          disabled={!announces}
          onChange={(next) => set('announceArrivalTime', next)}
        />
      </SettingRow>

      <NumberRow
        id={id('announcementRepeats')}
        name="announcementRepeats"
        value={settings.announcementRepeats}
        limits={telephonyLimits.announcementRepeats}
        unit={t('telephony.unitTimes')}
        disabled={!announces}
        onChange={(value) => set('announcementRepeats', value)}
      />

      <SettingRow label={t('telephony.holdMusicClass')} help={t('telephony.holdMusicClassHelp')}>
        <Input
          id={id('holdMusicClass')}
          type="text"
          aria-label={t('telephony.holdMusicClass')}
          value={settings.holdMusicClass}
          placeholder="default"
          disabled={!holds}
          className={DIMMED}
          onChange={(e) => set('holdMusicClass', e.target.value)}
        />
      </SettingRow>

      <SettingRow label={t('telephony.promptSet')} help={t('telephony.promptSetHelp')} last>
        <Input
          id={id('promptSet')}
          type="text"
          aria-label={t('telephony.promptSet')}
          value={settings.promptSet}
          placeholder="taxi/bg"
          disabled={!announces}
          className={DIMMED}
          onChange={(e) => set('promptSet', e.target.value)}
        />
      </SettingRow>
    </>
  );
}

/**
 * One whole number with its own range, its label and its explanation from its name.
 *
 * The name is what the keys are built from, so it is the field's own name and not a label:
 * renaming it silently empties two strings the key test cannot see.
 */
function NumberRow({
  id,
  name,
  value,
  limits,
  unit,
  disabled,
  note,
  onChange,
}: {
  id: string;
  name: string;
  value: number;
  limits: { min: number; max: number };
  unit: string;
  disabled: boolean;
  /** A sentence under the explanation, worked out from what has been typed. */
  note?: string;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <SettingRow
      label={t(`telephony.${name}`)}
      help={
        <>
          <div>{t(`telephony.${name}Help`)}</div>
          {note === undefined ? null : (
            <div className="mt-1 text-ink-2" role="note">
              {note}
            </div>
          )}
        </>
      }
    >
      <Measured
        id={id}
        type="number"
        inputMode="numeric"
        aria-label={t(`telephony.${name}`)}
        unit={unit}
        value={value}
        min={limits.min}
        max={limits.max}
        disabled={disabled}
        className={DIMMED_GROUP}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
    </SettingRow>
  );
}
